/*
 * Copyright The Adaptive Palette copyright holders
 * See the AUTHORS.md file at the top-level directory of this distribution and at
 * https://github.com/inclusive-design/adaptive-palette/raw/main/AUTHORS.md.
 *
 * Licensed under the New BSD license. You may not use this file except in
 * compliance with this License.
 *
 * You may obtain a copy of the License at
 * https://github.com/inclusive-design/adaptive-palette/blob/main/LICENSE
 */
import { JsonPaletteType, SymbolEncodingType, ContentSignalDataType, ModifierInfoType } from "./index.d";

/**
 * Global Utility Functions
 */

/**
 * Helper function to construct a single grid property rule.
 */
function buildGridProperty(property: string, start?: number, span?: number): string {
  const value = [start, span !== undefined ? `span ${span}` : undefined]
    .filter(val => val !== undefined)
    .join(" / ");
    
  return value ? `${property}: ${value};` : "";
}

/**
 * Generate the grid css.
 * @param {number} columnStart - The number of the column that the grid item will start from.
 * @param {number} columnSpan - The number of columns that the item will span across.
 * @param {number} rowStart - The number of the row that the grid item will start from.
 * @param {number} rowSpan - The number of rows that the item will span across.
 * @return {String} - The grid css.
 */
export function generateGridStyle(columnStart?: number, columnSpan?: number, rowStart?: number, rowSpan?: number): string {
  const colStyle = buildGridProperty("grid-column", columnStart, columnSpan);
  const rowStyle = buildGridProperty("grid-row", rowStart, rowSpan);

  return colStyle + rowStyle;
}

/**
 * Use the text-to-speech to announce the given text. If the previous announcement is still going
 * on, cancel it.
 * @param {String} text - The text to be announced.
 */
export function speak(text: string): void {
  // If the text-to-speech feature is unavailable, do nothing. This happens when running node tests.
  if (!window.speechSynthesis) {
    return;
  }

  // Cancel the previous announcement
  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel();
  }
  // Announce the current text
  const utterThis = new SpeechSynthesisUtterance(text);
  window.speechSynthesis.speak(utterThis);
}

/**
 * Load a palette from the given JSON file using `fetch()`. The location of the
 * JSON file is provided as a variable. If the loading fails, a console error with
 * detailed error message is reported.
 *
 * @param {String} jsonFilePath - Path of the JSON file to load, without the
 *                                ".json" extension (added herein).
 * @return {JsonPaletteType}    - The palette itself, or `null` if it could not be
 *                                loaded.
 */
export async function loadPaletteFromJsonFile (jsonFilePath: string): Promise<JsonPaletteType | undefined> {
  try {
    const response = await fetch(jsonFilePath);
    if (!response.ok) {
      console.error(`Error loading ${jsonFilePath}: ${response.status}`);
    }
    return await response.json() as JsonPaletteType;
  } catch (error) {
    console.error(`Error loading ${jsonFilePath}:, ${String(error)}`);
  }
}

/**
 * Given a current set of Bliss-words, the caret position and a Bliss-word to
 * add, insert the new word at the caret position in the set of symbols and
 * update to the new caret position.
 * @param {SymbolEncodingType} wordToAdd   - The new Bliss-word to add.
 * @param {SymbolEncodingType[]} symbolSet - The set of Bliss-words to add to.
 * @param {number} caretPos                - The insertion point within
 *                                           `symbolSet`.
 * @return {ContentSignalDataType} - the modified symbol set and new position of
 *                                   the insertion caret.
 */
export function insertWordAtCaret (wordToAdd: SymbolEncodingType, symbolSet: SymbolEncodingType[], caretPos: number ): ContentSignalDataType {
  let newSymbolSet;
  // If the `caretPos` is the last symbol in the `symbolSet`, append the new
  // `wordToAdd`.  If the `caretPos` is somwhere within the `symbolSet`, put the
  // new symbol right after the `caretPos`.  In both cases add one to the caret
  // position.
  const newCaretPos = caretPos + 1;
  if (caretPos === symbolSet.length-1) {
    newSymbolSet = {
      payloads: [...symbolSet, wordToAdd],
      caretPosition: newCaretPos
    };
  }
  else {
    symbolSet.splice(newCaretPos, 0, wordToAdd);
    newSymbolSet = {
      payloads: symbolSet,
      caretPosition: newCaretPos
    };
  }
  return newSymbolSet;
}

/**
 * Returns the inputted value constrained to the `min` and `max` values.
 * The returned value will:
 *  - `min` if `value` was less than `min`
 *  - `max` if the `value` was greater than `max`
 *  - `value` if it fell within the `min` `max` range.
 *
 * @param {number} value - The value to evaluate
 * @param {number} min - The minimum value to be returned
 * @param {number} max - The maximum value to be returned
 * @returns {number} - The constrained value
 */
export function clamp (value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Rebuild a label by folding a symbol's tracked modifiers around a base label in the
 * order they were applied.
 * @param {string} baseLabel - The base label to wrap, with no modifier text applied.
 * @param {ModifierInfoType[]} [modifierInfo] - The modifiers to fold in, in application order.
 * @returns {string} - The base label wrapped in every tracked modifier.
 */
export function applyModifiersToLabel (baseLabel: string, modifierInfo?: ModifierInfoType[]): string {
  return (modifierInfo ?? []).reduce(
    (label, modifier) => modifier.isPrepended ? `${modifier.modifierGloss} ${label}` : `${label} ${modifier.modifierGloss}`,
    baseLabel
  );
}

/**
 * Collapse a single-number composition array (e.g. `[1433]`) to the bare number `1433`.
 * Multi-element and string-containing arrays pass through unchanged.
 * @param {number|(number|string)[]} composition - The composition to normalize.
 * @returns {number|(number|string)[]} - The normalized composition.
 */
export function normalizeComposition (composition: number | (number|string)[]): number | (number|string)[] {
  return Array.isArray(composition) && composition.length === 1 && typeof composition[0] === "number" ? composition[0] : composition;
}

/**
 * Substitute `{{name}}` placeholders in a prompt template. Placeholders with no matching
 * value are left in place.
 * @param {string} template - The template text.
 * @param {Record<string, string>} values - Placeholder values by name.
 * @returns {string}
 */
export function renderTemplate (template: string, values: Record<string, string>): string {
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (placeholder, name: string) => (name in values ? values[name] : placeholder)
  );
}

/**
 * Render a line-per-field prompt template, dropping every line whose placeholders are all
 * empty. This lets one template cover prompts where some fields are unknown, instead of one
 * template per combination. A line holding a mix of empty and non-empty placeholders is
 * kept, so an empty field cannot take a filled one down with it. A placeholder with no
 * matching value at all is left in place, as in `renderTemplate()`, and its line is kept.
 * @param {string} template - The template text, one field per line.
 * @param {Record<string, string>} values - Placeholder values by name.
 * @returns {string}
 */
export function renderPromptLines (template: string, values: Record<string, string>): string {
  return template
    .split("\n")
    .filter((line) => {
      const placeholders = [...line.matchAll(/\{\{(\w+)\}\}/g)];
      return placeholders.length === 0 || !placeholders.every(
        ([, name]) => name in values && values[name].trim().length === 0
      );
    })
    .map((line) => renderTemplate(line, values))
    .join("\n");
}
